// Copyright (c) 2024-2026 Noushad Khalid / Codeteki. All rights reserved.
// Licensed under the Elastic License 2.0 — see LICENSE for details.

import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { useAnalytics } from "./hooks/use-analytics";
import { useEffect, lazy, Suspense } from "react";
import { initGA } from "./lib/analytics";
import Header from "./components/Header";
import Home from "./pages/Home";

// Lazy load UI components that use heavy dependencies (radix-ui, floating-ui)
const Toaster = lazy(() => import("./components/ui/toaster").then(m => ({ default: m.Toaster })));
const TooltipProvider = lazy(() => import("./components/ui/tooltip").then(m => ({ default: m.TooltipProvider })));

// Footer is below the fold - lazy load it
const Footer = lazy(() => import("./components/Footer"));

// Lazy load non-critical pages for code splitting
const Services = lazy(() => import("./pages/Services"));
const ServiceDetail = lazy(() => import("./pages/ServiceDetail"));
const SEOServicesPage = lazy(() => import("./pages/SEOServicesPage"));
const AIToolsPage = lazy(() => import("./pages/AIToolsPage"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const FAQPage = lazy(() => import("./pages/FAQ"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const DeleteAccount = lazy(() => import("./pages/DeleteAccount"));
const Demos = lazy(() => import("./pages/Demos"));
const AIChatWidget = lazy(() => import("./pages/AIChatWidget"));
const InstantQuoteWidget = lazy(() => import("./pages/InstantQuoteWidget"));
const ServiceReminders = lazy(() => import("./pages/ServiceReminders"));
const ServiceReminderIndustryLanding = lazy(() => import("./pages/ServiceReminderIndustryLanding"));
const Spanner = lazy(() => import("./pages/Spanner"));
const TradeQuoteLanding = lazy(() => import("./pages/TradeQuoteLanding"));
const QuoteWidgetRouter = lazy(() => import("./pages/QuoteWidgetRouter"));
// const WhatsAppAI = lazy(() => import("./pages/WhatsAppAI")); // HIDDEN: pending Meta app approval
const GetStarted = lazy(() => import("./pages/GetStarted"));
const FreeAuditPage = lazy(() => import("./pages/FreeAuditPage"));
const AuditMethodologyPage = lazy(() => import("./pages/AuditMethodologyPage"));
const BudgetOSPage = lazy(() => import("./pages/BudgetOSPage"));
const WeddingOSPage = lazy(() => import("./pages/WeddingOSPage"));
const PurchaseSuccessPage = lazy(() => import("./pages/PurchaseSuccessPage"));
const LocalLandingPage = lazy(() => import("./pages/LocalLandingPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const GlobalSaasLandingPage = lazy(() => import("./pages/GlobalSaasLandingPage"));
const AIChatbotForTradies = lazy(() => import("./pages/AIChatbotForTradies"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load ChatWidget as it's not critical for initial render
const ChatWidget = lazy(() => import("./components/ChatWidget"));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="w-10 h-10 border-4 border-gray-200 border-t-[#f9cb07] rounded-full animate-spin"></div>
  </div>
);

function Router() {
  const [location] = useLocation();
  useAnalytics();

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return (
    <>
      {/* Skip Navigation Links for Accessibility */}
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <a href="#navigation" className="skip-link">
        Skip to navigation
      </a>
      <Header />
      <main id="main-content" tabIndex={-1}>
        <Suspense fallback={<PageLoader />}>
          <Switch>
            <Route path="/" component={Home} />
            <Route path="/services" component={Services} />
            <Route path="/services/seo-services" component={SEOServicesPage} />
            <Route path="/services/:serviceId" component={ServiceDetail} />
            <Route path="/ai-tools" component={AIToolsPage} />
            <Route path="/ai-chat-widget" component={AIChatWidget} />
            <Route path="/instant-quote-widget/:trade" component={QuoteWidgetRouter} />
            <Route path="/instant-quote-widget" component={InstantQuoteWidget} />
            <Route path="/service-reminders" component={ServiceReminders} />
            <Route path="/service-reminders/:industry" component={ServiceReminderIndustryLanding} />
            <Route path="/spanner" component={Spanner} />
            <Route path="/ai-chat-widget/:market" component={GlobalSaasLandingPage} />
            <Route path="/ai-chatbot-for-tradies" component={AIChatbotForTradies} />
            <Route path="/instant-quote-widget-for-tradies" component={() => <TradeQuoteLanding trade="tradies" />} />
            {/* <Route path="/whatsapp-ai" component={WhatsAppAI} /> */}{/* HIDDEN: pending Meta app approval */}
            <Route path="/demos" component={Demos} />
            <Route path="/blog" component={Blog} />
            <Route path="/blog/:slug" component={BlogDetail} />
            <Route path="/contact" component={Contact} />
            <Route path="/about" component={About} />
            <Route path="/faq" component={FAQPage} />
            <Route path="/privacy-policy" component={PrivacyPolicy} />
            {/* App Store / Play Store listings link here — same policy, Section 13 covers the app specifically */}
            <Route path="/app-privacy"><Redirect to="/privacy-policy" /></Route>
            <Route path="/terms-of-service" component={TermsOfService} />
            <Route path="/delete-account" component={DeleteAccount} />
            <Route path="/get-started" component={GetStarted} />
            <Route path="/free-website-audit" component={FreeAuditPage} />
            <Route path="/free-website-audit/methodology" component={AuditMethodologyPage} />
            <Route path="/ai-tools/budgetos" component={BudgetOSPage} />
            <Route path="/ai-tools/weddingos" component={WeddingOSPage} />
            <Route path="/purchase/success" component={PurchaseSuccessPage} />
            <Route path="/vs/:slug" component={ComparisonPage} />
            <Route path="/:service/:suburb" component={LocalLandingPage} />
            <Route component={NotFound} />
          </Switch>
        </Suspense>
      </main>
      <Suspense fallback={<div className="bg-black h-64" />}>
        <Footer />
      </Suspense>
      {location !== "/ai-chat-widget" && /* location !== "/whatsapp-ai" && */ (
        <Suspense fallback={null}>
          <ChatWidget />
        </Suspense>
      )}
    </>
  );
}

function App() {
  // Initialize Google Analytics on app start
  useEffect(() => {
    initGA();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <Suspense fallback={null}>
          <TooltipProvider>
            <Suspense fallback={null}>
              <Toaster />
            </Suspense>
            <Router />
          </TooltipProvider>
        </Suspense>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
